import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CryptoJS from "crypto-js";

import { getCredenciales, deleteCredencial } from "../services/api";
import PinModal from "../components/PinModal";
import { registrarVisualizacion } from "../services/auditoria";

import LoaderPremiumLogo from "../components/LoaderPremiumLogo";

export default function CredencialesView() {
  const [credenciales, setCredenciales] = useState([]);
  const [credencialActual, setCredencialActual] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);

  const [search, setSearch] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("Todas");

  const [loading, setLoading] = useState(true);

  const SECRET_KEY = "IP_DEPOT_KEY_2025_SECURE";

  useEffect(() => {
    cargar();
  }, []);

  const cargar = async () => {
    try {
      setLoading(true);
      const res = await getCredenciales();
      setCredenciales(res.data || []);
    } catch (err) {
      console.error("Error cargando credenciales:", err);
    } finally {
      setLoading(false);
    }
  };

  // ===========================
  // DESENCRIPTAR (BLINDADO)
  // ===========================
  const desencriptar = (texto) => {
    try {
      if (!texto || typeof texto !== "string") return "********";

      const bytes = CryptoJS.AES.decrypt(texto, SECRET_KEY);
      const result = bytes.toString(CryptoJS.enc.Utf8);
      return result || "********";

    } catch (e) {
      console.error("Error desencriptando:", e);
      return "********";
    }
  };

  const solicitarPIN = (cred) => {
    setCredencialActual(cred);
    setShowPinModal(true);
  };

  const mostrarTemporal = (cred) => {
    if (!cred) return;

    cred._showPassword = true;
    registrarVisualizacion(cred.id, cred.servicio);
    setCredenciales([...credenciales]);

    setTimeout(() => {
      cred._showPassword = false;
      setCredenciales([...credenciales]);
    }, 4000);
  };

  const copiar = (texto) => {
    if (texto) navigator.clipboard.writeText(texto);
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Deseas eliminar esta credencial?")) return;
    try {
      await deleteCredencial(id);
      cargar();
    } catch (err) {
      console.error("Error eliminando credencial:", err);
    }
  };

  // ===========================
  // FILTROS
  // ===========================
  const categorias = [
    "Todas",
    ...Array.from(
      new Set(
        (credenciales || [])
          .map((c) => c?.categoria)
          .filter((x) => x && x.trim() !== "")
      )
    ),
  ];

  const filtrarCredenciales = () => {
    const texto = search.toLowerCase().trim();

    return (credenciales || []).filter((c) => {
      if (!c) return false;

      if (categoriaFiltro !== "Todas" && (c.categoria || "") !== categoriaFiltro) {
        return false;
      }

      if (!texto) return true;

      const blob = [
        c.servicio,
        c.usuario,
        c.url,
        c.notas,
        c.categoria,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return blob.includes(texto);
    });
  };

  const listaFiltrada = filtrarCredenciales();

  if (loading) return <LoaderPremiumLogo />;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 py-10 px-6">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
              🔐 Claves & Contraseñas
            </h1>
            <p className="text-slate-600 mt-1 text-sm">
              Vault seguro con encriptación avanzada.
            </p>
          </div>

          <Link
            to="/credenciales/nueva"
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-lg hover:bg-indigo-700 hover:shadow-xl transition"
          >
            + Nueva credencial
          </Link>
        </div>

        {/* BUSCADOR */}
        <section className="bg-white/80 backdrop-blur border border-slate-200 rounded-2xl shadow-md px-4 py-4 md:px-6 md:py-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">

          <div className="w-full md:w-2/3 flex items-center gap-3">
            <input
              type="text"
              placeholder="Buscar por servicio, usuario, URL o notas..."
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase">Categoría</span>
            <select
              className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-slate-50"
              value={categoriaFiltro}
              onChange={(e) => setCategoriaFiltro(e.target.value)}
            >
              {categorias.map((cat) => (
                <option key={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </section>

        {/* GRID */}
        {listaFiltrada.length === 0 ? (
          <div className="bg-white/80 backdrop-blur border border-dashed border-slate-300 rounded-2xl p-10 text-center text-slate-500 text-sm">
            No se encontraron credenciales.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-7">
            {listaFiltrada.map((c) => {
              if (!c) return null;

              const pass = desencriptar(c.contrasena);

              return (
                <div
                  key={c.id}
                  className="group relative backdrop-blur-xl bg-white/80 border border-slate-200 shadow-xl rounded-2xl p-6 hover:shadow-2xl transition"
                >
                  {/* Eliminar */}
                  <button
                    onClick={() => eliminar(c.id)}
                    className="absolute top-4 right-4 text-xs text-rose-400 hover:text-rose-600"
                  >
                    ✕
                  </button>

                  {/* Icono */}
                  <div className="w-12 h-12 bg-indigo-100 text-indigo-600 flex items-center justify-center rounded-xl text-2xl mb-4">
                    🔑
                  </div>

                  <h3 className="text-base font-bold text-slate-900">
                    {c.servicio || "Servicio sin nombre"}
                  </h3>

                  <p className="text-sm mt-3">
                    <strong>Usuario:</strong> {c.usuario || "N/D"}
                  </p>

                  {/* CONTRASEÑA */}
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase">
                      Contraseña
                    </p>

                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type={c._showPassword ? "text" : "password"}
                        value={c._showPassword ? pass : "************"}
                        readOnly
                        className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm w-full bg-slate-50"
                      />

                      {!c._showPassword && (
                        <button
                          onClick={() => solicitarPIN(c)}
                          className="text-slate-500 hover:text-slate-900 text-lg"
                        >
                          👁️
                        </button>
                      )}

                      {c._showPassword && (
                        <button
                          onClick={() => copiar(pass)}
                          className="text-indigo-600 hover:text-indigo-800 text-lg"
                        >
                          📋
                        </button>
                      )}
                    </div>
                  </div>

                  {/* URL */}
                  {c.url && (
                    <a
                      href={c.url}
                      target="_blank"
                      className="block text-indigo-600 hover:underline text-xs mt-3"
                    >
                      {c.url}
                    </a>
                  )}

                  {/* Notas */}
                  {c.notas && (
                    <p className="mt-3 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
                      {c.notas}
                    </p>
                  )}

                  <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100">
                    <Link
                      to={`/credenciales/editar/${c.id}`}
                      className="text-xs font-semibold text-indigo-600 hover:underline"
                    >
                      Editar
                    </Link>
                    <span className="text-[11px] text-slate-400">
                      ID: {c.id?.slice(0, 6) ?? "—"}…
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {showPinModal && (
        <PinModal
          onClose={() => setShowPinModal(false)}
          onSuccess={() => {
            mostrarTemporal(credencialActual);
            setShowPinModal(false);
          }}
        />
      )}
    </div>
  );
}
